import { useEffect } from "react";
import { Button, CalloutBanner, Icon } from "@alto-avios/alto-ui";
import { usePurchaseComponents } from "@contexts/PurchaseComponentsContext";
import { formatCurrency } from "@helpers/formatCurrency";
import { formatNumber } from "@helpers/formatNumber";
import { replacePlaceholders } from "@helpers/replacePlaceholders";
import { useBreakpoint } from "@hooks/useBreakpoint";

import { useContent } from "@api/contentful/content";
import type { BoostCalloutBannerContent } from "@api/contentful/parsers/boostCalloutBanner";
import type { BoosterCalloutBannerMultiplier, BoosterCalloutBannerResponse } from "@api/products";
import { useBoosterCalloutBanner } from "@api/products";

import type { BoostCalloutBannerProps } from "./BoostCalloutBanner.types";

const selectMultiplier = (
  bannerSummary: BoosterCalloutBannerResponse,
  multipliersAvailable: Array<BoosterCalloutBannerMultiplier>,
  content: BoostCalloutBannerContent,
) => {
  if (content.selectionMode === "bestPrice") {
    return multipliersAvailable.find((m) => m.multiplyBy === bannerSummary.bestPriceMultiplier);
  }

  if (content.selectionMode === "multiplier" && content.preferredMultiplier !== undefined) {
    return multipliersAvailable.find((m) => m.multiplyBy === content.preferredMultiplier);
  }

  return multipliersAvailable.find((m) => m.multiplyBy === bannerSummary.bestValueMultiplier);
};

/**
 * Renders the shared buy-Avios booster callout banner when the member is eligible
 * and the required Contentful configuration is available.
 */
export const BoostCalloutBanner = ({
  enabled = true,
  title: titleOverride,
  ctaLabel: ctaLabelOverride,
  description: descriptionOverride,
  headingLevel,
  onBoostClick,
  onImpression,
  className,
}: BoostCalloutBannerProps) => {
  const breakpoint = useBreakpoint();
  const { locale } = usePurchaseComponents();
  const { data: bannerSummary, isLoading } = useBoosterCalloutBanner(enabled);
  const { data: content } = useContent("boostCalloutBanner", { enabled });

  useEffect(() => {
    if (bannerSummary?.isEligible && onImpression) {
      onImpression();
    }
  }, [bannerSummary?.isEligible, onImpression]);

  if (!content) {
    return null;
  }

  const multipliersAvailable = bannerSummary?.multipliersAvailable ?? [];
  const resolvedMultiplier = bannerSummary
    ? (selectMultiplier(bannerSummary, multipliersAvailable, content) ?? multipliersAvailable[0])
    : undefined;

  if (!enabled || isLoading || !bannerSummary?.isEligible || !resolvedMultiplier) {
    return null;
  }

  const layout = { mobile: content.mobileLayout, tablet: content.tabletLayout, desktop: content.desktopLayout }[
    breakpoint
  ];
  const buttonSize = {
    mobile: content.mobileButtonSize,
    tablet: content.tabletButtonSize,
    desktop: content.desktopButtonSize,
  }[breakpoint];

  const formattedAvios = formatNumber({ value: resolvedMultiplier.totalAvios, locale });
  const formattedPrice = bannerSummary.currency
    ? formatCurrency({
        value: resolvedMultiplier.totalPrice,
        locale,
        currency: bannerSummary.currency,
        removeDecimalsIfWhole: true,
      })
    : "";

  const resolvedTitle =
    titleOverride ??
    (content.title ? replacePlaceholders(content.title, { avios: formattedAvios, price: formattedPrice }) : "");
  const resolvedCtaLabel = ctaLabelOverride ?? content.ctaLabel;
  const resolvedDescription = descriptionOverride ?? content.description;

  const handleBoostClick = () => {
    onBoostClick?.();
    globalThis.open(content.ctaUrl, "_blank");
  };

  return (
    <div className={className}>
      <CalloutBanner
        title={resolvedTitle}
        description={resolvedDescription}
        styleVariant={content.styleVariant}
        emphasis={content.emphasis}
        headerLayout={layout}
        headingLevel={headingLevel}
        customAsset={
          <Icon
            iconName={content.iconName ?? "arrow-up-right-dots"}
            iconPrefix={content.iconPrefix}
            iconSize={content.iconSize}
            padding="roomy"
            color={content.iconColor}
            aria-hidden="true"
          />
        }
      >
        <CalloutBanner.Action>
          <Button
            onPress={handleBoostClick}
            size={buttonSize}
            styleVariant={content.buttonStyleVariant}
            emphasis={content.buttonEmphasis}
          >
            {resolvedCtaLabel}
          </Button>
        </CalloutBanner.Action>
      </CalloutBanner>
    </div>
  );
};
